<template>
  <v-container fluid py-0>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-container fluid pt-0>
          <v-row align="center">
            <div>
              <h1 class="headline">Clients</h1>
            </div>
            <v-col class="py-0">
              <list-filters
                :filters="filters"
                label="Find Client"
                ref-name="clientsListFilters"
                @searchParamsChanged="searchParamsChanged($event)"
                :expansionPanel="false"
              ></list-filters>
            </v-col>
            <div>
              <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
            </div>
            <div>
              <list-visible-columns-and-filter
                :visible-columns="visibleColumns"
                :selectable-headers="selectableHeaders"
                v-model="search"
                @change="onVisibleColumnsChange"
              ></list-visible-columns-and-filter>
            </div>
            <div>
              <v-tooltip
                v-if="$store.getters.hasAccessTo('clients:create')"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="ml-2"
                    fab
                    dark
                    color="primary"
                    @click="onCreateNew"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Create New Client</span>
              </v-tooltip>
            </div>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <div v-if="showAgGrid" key="ag-grid">
      <ag-grid-vue
        class="ag-theme-alpine"
        id="ag-client-list"
        :columnDefs="agGridColumns"
        :rowData="items"
        rowSelection="multiple"
        :context="agContext"
        @grid-ready="onAgGridReady"
        @columnMoved="onAgColumnMoved"
        @columnResized="onAgColumnResized"
        @sortChanged="onAgSortChanged"
        @selection-changed="onAgSelectionChanged"
        @newColumnsLoaded="onAgNewColumns"
        suppressDragLeaveHidesColumns
      >
      </ag-grid-vue>
    </div>
    <v-data-table
      v-else
      :headers="filteredHeaders"
      :items="items"
      :loading="$apollo.loading"
      :search="search"
      show-select
      v-model="selected"
      item-key="id"
      class="elevation-1"
      v-bind="pagingOptions"
      fixed-header
      hide-default-footer
      @update:options="onListOptionsUpdate"
      :height="gridHeight"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
    >
      <template v-slot:item.actions="{ item }">
        <default-list-actions
          :item="item"
          :hide-view="hideView"
          :hide-remove="hideRemove"
          :hide-edit="hideEdit"
          @deleteItem="deleteItem"
          @editItem="editItem"
          @viewItem="editItem"
        ></default-list-actions>
      </template>
      <template v-slot:footer>
        <list-actions
          v-if="selected.length > 0"
          :selected="selected"
          list="clients"
          :columns="visibleColumns"
          :filter="searchParams"
          @close="selected = []"
          :hide-export="!$store.getters.hasAccessTo('clients:export')"
        >
        </list-actions>
        <v-row justify="center" v-if="selected.length > 0">
          <h3 class="py-2 caption">{{ selected.length }} record(s) selected</h3>
        </v-row>
      </template>
    </v-data-table>

    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="clients"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('clients')"
    ></get-more-results>
  </v-container>
</template>
<script>
import { ListMixin } from "../../../mixins/ListMixin";
import {
  ALL_CLIENTS_SEARCH_QUERY,
  CLIENT_DELETE_MUTATION,
} from "../../../constants/graphql";
import GetMoreResults from "../shared/GetMoreResults";
import DefaultListActions from "../shared/DefaultListActions";
import ListFilters from "../shared/list-filters/ListFilters";
import ListActions from "../shared/list-actions/ListActions";
import clientsFilters from "./clients-filters";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import { removeGQLTypename } from "../../../helpers";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../shared/AgGridSwitch";
import AgGridListMixin from "../../../mixins/AgGridListMixin";
export default {
  name: "ClientList",

  props: {
    refName: {
      type: String,
      default: () => "clients-list",
    },
    refNameColumns: {
      type: String,
      default: () => "clients-list-cols",
    },
  },

  mixins: [ListMixin, AgGridListMixin],
  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    ListActions,
    AgGridVue,
    AgGridSwitch,
  },

  data() {
    return {
      selected: [],
      clients: {},
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "name",
        "surname",
        "email",
        "actions",
      ]),
      clientsFilters: clientsFilters,
      headers: [
        {
          text: "Title",
          value: "title",
          name: "title",
        },
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Surname",
          value: "surname",
          name: "surname",
        },
        {
          text: "Email",
          value: "email",
          name: "email",
        },
        {
          text: "Code",
          value: "code",
          name: "code",
        },
        {
          text: "Telephone",
          value: "telephone",
          name: "telephone",
        },
        {
          text: "Mobile",
          value: "mobile",
          name: "mobile",
        },
        {
          text: "Address",
          value: "address",
          name: "address",
        },
        {
          text: "Notes",
          value: "notes",
          name: "notes",
        },
        {
          text: "Nationality",
          value: "nationality",
          name: "nationality",
        },
        {
          text: "Status",
          value: "clientStatusName",
          name: "clientStatusName",
        },
        {
          text: "Type",
          value: "clientTypeName",
          name: "clientTypeName",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
        },
      ],
    };
  },

  computed: {
    filters() {
      return clientsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    items() {
      return this.clients && this.clients.edges
        ? this.clients.edges.map(removeGQLTypename)
        : [];
    },
    hideView() {
      return !this.$store.getters.hasAccessTo("clients:view_only");
    },
    hideRemove() {
      return (
        !this.$store.getters.hasAccessTo("clients:delete") ||
        this.$store.getters.hasAccessTo("clients:view_only")
      );
    },
    hideEdit() {
      return (
        !this.$store.getters.hasAccessTo("clients:edit") ||
        this.$store.getters.hasAccessTo("clients:view_only")
      );
    },
  },

  methods: {
    editItem(item) {
      this.$router.push("/client/" + item.id + "/edit");
    },
    onCreateNew() {
      this.$router.push("/client/new");
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: CLIENT_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Client removed." });

      if (this.$apollo.queries.clients != null) {
        this.$apollo.queries.clients.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
      //console.log(err);
    },
  },

  apollo: {
    clients: {
      query: ALL_CLIENTS_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          filter: this.searchParams,
          sort: this.sort,
          first: this.first,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.clients.totalCount;
      },
    },
  },
};
</script>
<style scoped>
#ag-client-list {
  height: calc(100vh - 220px);
}
</style>


// WEBPACK FOOTER //
// src/components/gt/clients/ClientList.vue